import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { AppTabList, type TabType } from "@components/AppTabList.tsx";
import AppCard from "@components/AppCard.tsx";
import { TabContext, TabPanel } from "@mui/lab";
import { Stack, Tab } from "@mui/material";
import { Icon } from "@iconify/react";
import { requireRole } from "@/auth/requireRole.ts";
import { ROLE_ADMIN } from "@/constants";
import { useCurrentHrdFormulaireVersion } from "@contexts/HrdFormulaireVersionContext.tsx";
import { HrdFormulaireDesigner } from "@components/hrd/formulaires/HrdFormulaireDesigner.tsx";
import { HrdFormulaireOverviewTab } from "@components/hrd/formulaires/HrdFormulaireOverviewTab.tsx";
import HrdCasesDataGrid from "@components/hrd/cases/HrdCasesDataGrid.tsx";
import { getHasHrdAccess } from "@/auth/hasHrdAccess.tsx";
import { HrdFormulaireVersionStatusAlert } from "@components/hrd/HrdFormulaireVersionStatusAlert.tsx";
import { api } from "@api/api.ts";
import { useDispatch } from "react-redux";

export const Route = createFileRoute("/_dashboard/hrd/formulaires/$uuid/$tab")({
  beforeLoad: (args) => {
    const { context } = args;
    requireRole([ROLE_ADMIN])(args);
    const hasHrdAccess = getHasHrdAccess(
      context.session!.user!.hrdAccessStatus
    );

    // Check HRD access for non-admins only
    if (!context.session?.user?.isAdmin && !hasHrdAccess) {
      throw redirect({
        to: "/unauthorized",
        search: {
          redirect: location.href,
        },
      });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const hrdFormulaire = useCurrentHrdFormulaireVersion();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleDesignerSave = async () => {
    // INVALIDATE HRD FORMULAIRE VERSIONS CACHE
    dispatch(
      api.util.invalidateTags([
        {
          type: "HrdFormulaire",
        },
      ])
    );
    await navigate({ to: "/hrd/formulaires/$uuid/$tab", params: { uuid: hrdFormulaire.uuid, tab: "overview" }});
  }

  const TABS: TabType[] = [
    {
      label: "Informations",
      value: "overview",
      href: `/hrd/formulaires/${hrdFormulaire.uuid}/overview`,
      icon: "tabler:file-text",
      dataPw: "overview-tab",
      children: (
        <HrdFormulaireOverviewTab hrdFormulaireVersion={hrdFormulaire} />
      ),
    },
    {
      label: "Formulaire",
      value: "formulaire",
      href: `/hrd/formulaires/${hrdFormulaire.uuid}/formulaire`,
      icon: "tabler:template",
      dataPw: "formulaire-tab",
      children: (
        <Stack gap={1}>
          <HrdFormulaireVersionStatusAlert status={hrdFormulaire.status} />
          <AppCard sx={{ p: 0 }}>
            <HrdFormulaireDesigner hrdFormulaire={hrdFormulaire} onSave={handleDesignerSave}/>
          </AppCard>
        </Stack>
      ),
    },
    ...(hrdFormulaire.status.id !== "DRAFT"
      ? [
          {
            label: "Cas",
            value: "cases",
            href: `/hrd/formulaires/${hrdFormulaire.uuid}/cases`,
            icon: "tabler:database",
            children: (
              <AppCard>
                <HrdCasesDataGrid
                  hideButtons
                  customQueryParams={{
                    ["hrdFormulaireVersion.uuid"]: hrdFormulaire.uuid,
                  }}
                />
              </AppCard>
            ),
          },
        ]
      : []),
  ];

  const { tab } = Route.useParams();

  return (
    <TabContext value={tab}>
      <AppTabList>
        {TABS.map((tab) => (
          <Tab
            component={Link}
            key={tab.value}
            label={tab.label}
            value={tab.value}
            href={tab.href ?? tab.value}
            {...(tab.icon && {
              icon: <Icon fontSize="18" icon={tab.icon} />,
            })}
            data-pw={tab.dataPw}
            iconPosition={"start"}
          />
        ))}
      </AppTabList>
      {TABS.map((tab) => (
        <TabPanel
          value={tab.value}
          key={tab.value}
          sx={{
            padding: 0,
          }}
        >
          {tab.children}
        </TabPanel>
      ))}
    </TabContext>
  );
}
